import { HotelType, OfferType, City } from '../types/chatTypes';
import { handleError } from '../../../../lib/utils/error';
import { searchHotels, getHotelOffers } from '../../../hotels/services/hotelSearchService';
import { SimplifiedHotel } from '../../types';
import { locationStringToCity } from '../utils/contextUtils';

/**
 * Service for hotel-related functionality
 */
export class HotelService {
  /**
   * Convert a location string to a City object
   */
  // async locationStringToCity(locationString: string): Promise<City | null> { ... function moved ... }

  /**
   * Search for hotels and return simplified results
   */
  async searchHotels(location: string): Promise<{ 
    simplifiedHotels: SimplifiedHotel[]; 
    conversationalResponse: string;
    error?: string;
  }> {
    console.log(`[searchAndDispatchHotels] Starting search for location: "${location}"`);
    
    const checkInDate = new Date();
    // Set default dates (e.g., a week from now for 2 nights)
    checkInDate.setDate(checkInDate.getDate() + 7);
    const checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkOutDate.getDate() + 2);
    const formattedCheckIn = checkInDate.toISOString().split('T')[0];
    const formattedCheckOut = checkOutDate.toISOString().split('T')[0];
    const adults = 2; // Default adults

    try {
      console.log(`Searching hotels in ${location} from ${formattedCheckIn} to ${formattedCheckOut}`);
      
      // Convert string location to City object using the imported function
      const city = await locationStringToCity(location);
      if (!city) {
        console.log(`Could not convert "${location}" to a valid City object`);
        return {
          simplifiedHotels: [],
          conversationalResponse: `I couldn't find "${location}" as a valid destination. Could you please provide a specific city name? For example, "New York", "London", or "Tokyo".`,
          error: 'invalid_location'
        };
      }
      
      // Validate coordinates to avoid the "Cannot search hotels without IATA code or geocoordinates" error
      if ((!city.latitude || !city.longitude) && !city.iataCode) {
        console.log(`[searchAndDispatchHotels] Missing coordinates or IATA code for "${city.name}"`);
        return {
          simplifiedHotels: [],
          conversationalResponse: `I couldn't find enough location information for "${city.name}". Could you please provide a more specific city name?`,
          error: 'missing_coordinates'
        };
      }

      // Create properly formatted parameters for searchHotels
      const searchParams = {
        city: city,
        checkIn: formattedCheckIn,
        checkOut: formattedCheckOut,
        adults: adults
      };
      
      console.log('[searchAndDispatchHotels] Calling searchHotels with params:', JSON.stringify(searchParams));
      const result = await searchHotels(searchParams);
      const hotels = result.hotels || [];
      console.log(`[searchAndDispatchHotels] searchHotels returned ${hotels?.length ?? 0} results.`);

      if (!hotels || hotels.length === 0) {
        console.log(`No hotels found for ${location}`);
        return {
          simplifiedHotels: [],
          conversationalResponse: `I couldn't find any available hotels in ${location} for those dates.`,
          error: 'no_hotels_found'
        };
      }

      console.log(`Found ${hotels.length} hotels. Fetching offers...`);

      // Fetch actual offers
      const hotelIds = hotels.map(h => h.hotelId);
      console.log(`[searchAndDispatchHotels] Calling getHotelOffers for ${hotelIds.length} hotel(s).`);
      const offersData = await getHotelOffers(hotelIds, formattedCheckIn, formattedCheckOut, adults, 1, 'USD');

      console.log(`Received offers for ${Object.keys(offersData).length} hotels.`);

      // Map results
      const simplifiedHotels: SimplifiedHotel[] = hotels.slice(0, 3).map(initialHotel => { // Rename to initialHotel for clarity
        const hotelId = initialHotel.hotelId; // Get ID from the initial search result
        const hotelOfferResponseData = offersData[hotelId];
        let actualOffersArray: OfferType[] | undefined = undefined;

        console.log(`[searchAndDispatchHotels] Raw offersData for Hotel ID ${hotelId}:`, JSON.stringify(hotelOfferResponseData, null, 2));

        // --- Extract rich hotel data object from the OFFERS response ---
        let richHotelData: any | null = null; // Use 'any' for flexibility or define a more specific type if possible
        if (hotelOfferResponseData && typeof hotelOfferResponseData === 'object' && hotelOfferResponseData !== null && (hotelOfferResponseData as any).hotel) {
             richHotelData = (hotelOfferResponseData as any).hotel; // Get the hotel object from the offer response
             console.log(`[searchAndDispatchHotels] Found rich hotel data in offers response for ${hotelId}`);
        } else {
             console.warn(`[searchAndDispatchHotels] Could not find rich hotel data in offers response for ${hotelId}`);
        }
        // --- End Rich Data Extraction ---

        if (hotelOfferResponseData) {
            if (Array.isArray(hotelOfferResponseData) && hotelOfferResponseData.length > 0) {
                if (hotelOfferResponseData[0] && Array.isArray(hotelOfferResponseData[0].offers)) {
                     actualOffersArray = hotelOfferResponseData[0].offers;
                }
            } else if (typeof hotelOfferResponseData === 'object' && hotelOfferResponseData !== null) {
                 if ('offers' in hotelOfferResponseData && Array.isArray((hotelOfferResponseData as any).offers)) {
                    actualOffersArray = (hotelOfferResponseData as any).offers;
                } else if ('available' in hotelOfferResponseData && !(hotelOfferResponseData as any).available) {
                     console.log(`[searchAndDispatchHotels] Hotel ID ${hotelId} marked as unavailable.`);
                } else {
                     console.log(`[searchAndDispatchHotels] Hotel ID ${hotelId} response object lacks valid 'offers' array.`);
                }
            }
        } else {
            console.log(`[searchAndDispatchHotels] No data found in offersData for Hotel ID: ${hotelId}`);
        }

        if (!actualOffersArray || actualOffersArray.length === 0) {
            actualOffersArray = undefined;
            if (hotelOfferResponseData && (hotelOfferResponseData as any)?.available !== false) {
                console.warn(`[searchAndDispatchHotels] Could not extract valid nested offers array for available Hotel ID: ${hotelId}`);
            }
        }

        // --- Create hotelForMapping PRIMARILY from richHotelData (from offers response) ---
        const hotelForMapping: HotelType = {
          hotelId: hotelId, // Use ID from initial search
          // Use data from the offers response first, fallback to initial data if needed/available
          name: richHotelData?.name || initialHotel.name || 'Unknown Hotel',
          // Ensure rating is handled correctly (it might be string or number in different places)
          rating: richHotelData?.rating !== undefined ? String(richHotelData.rating) : (initialHotel.rating !== undefined ? String(initialHotel.rating) : undefined),
          address: { // Build address object carefully
             cityName: richHotelData?.cityCode || initialHotel.address?.cityName, // Use cityCode from offers hotel data if available
             // You might need to add other address fields here if they exist in richHotelData
             // e.g., countryCode: richHotelData?.countryCode || initialHotel.address?.countryCode
          },
          // Use media/amenities directly from richHotelData if available
          media: richHotelData?.media || initialHotel.media,
          amenities: richHotelData?.amenities || initialHotel.amenities,
          // Use coordinates ONLY from the rich hotel data (offers response)
          latitude: typeof richHotelData?.latitude === 'number' ? richHotelData.latitude : undefined,
          longitude: typeof richHotelData?.longitude === 'number' ? richHotelData.longitude : undefined
        };
        
        // Pass the richer hotel object and the offers array to mapToSimplifiedHotel
        return this.mapToSimplifiedHotel(hotelForMapping, actualOffersArray);
      });

      console.log(`[searchAndDispatchHotels] Mapped to ${simplifiedHotels.length} simplified hotels for display.`);

      // Cast offersData to the expected type for extract functions
      const offersDataForSummary = offersData as Record<string, OfferType[]>;
      const roomPriceSummary = this.extractRoomPriceSummary(offersDataForSummary, hotels);
      
      // Create a conversational response
      let conversationalResponse = '';
      
      if (simplifiedHotels.length > 0) {
        // Get price range info
        const prices = this.extractPriceRanges(offersDataForSummary);
        
        // Build conversational response
        conversationalResponse = `I found ${Math.min(hotels.length, 3)} hotels in ${location} for your dates (${formattedCheckIn} to ${formattedCheckOut}).`;
        
        if (prices.minPrice && prices.maxPrice) {
          conversationalResponse += ` Prices range from $${prices.minPrice} to $${prices.maxPrice} per night.`;
        }
        
        if (roomPriceSummary) {
          conversationalResponse += ` ${roomPriceSummary}`;
        }
        
        return {
          simplifiedHotels,
          conversationalResponse
        };
      } else {
        // This might happen if hotels were found but no offers were available
        return {
          simplifiedHotels: [],
          conversationalResponse: `I found some hotels in ${location}, but couldn't get pricing details for those dates right now.`,
          error: 'no_offers'
        };
      }
    } catch (error) {
      console.error(`[searchAndDispatchHotels] Error during search/offer fetch for ${location}:`, error);
      handleError(error, `Hotel search failed for ${location}`);
      return {
        simplifiedHotels: [],
        conversationalResponse: `Sorry, I encountered an error trying to find hotel details for ${location}. Please try again later.`,
        error: 'search_error'
      };
    }
  }

  /**
   * Map from API hotel object to simplified hotel object
   */
  mapToSimplifiedHotel(hotel: HotelType, offers?: OfferType[]): SimplifiedHotel {
    let priceRange = 'Price unavailable'; // Default

    // **** The 'offers' parameter should NOW be the correct array of individual offers ****
    if (offers && Array.isArray(offers) && offers.length > 0) {
        console.log(`[mapToSimplifiedHotel] Processing ${offers.length} individual offers for Hotel ID: ${hotel.hotelId}`);

        let minPrice = Infinity;
        let minOfferData: { total: string, currency: string } | null = null;

        // Loop through the ACTUAL individual offers
        offers.forEach((individualOffer, index) => {
            try {
                // Access price directly on the individual offer object
                const rawTotal = individualOffer?.price?.total;
                const currency = individualOffer?.price?.currency;
                // console.log(`[mapToSimplifiedHotel] Individual Offer ${index} raw price: ${rawTotal}, currency: ${currency}`); // Keep for debugging

                if (rawTotal && currency && String(rawTotal).trim() !== "") {
                    let processString = String(rawTotal).replace(',', '.');
                    const numericString = processString.replace(/[^0-9.-]+/g, "");
                    const priceValue = parseFloat(numericString);

                    if (!isNaN(priceValue)) {
                        if (priceValue < minPrice) {
                            minPrice = priceValue;
                            minOfferData = { total: rawTotal, currency: currency };
                        }
                    } else {
                        console.warn(`[mapToSimplifiedHotel] Could not parse price for comparison: ${rawTotal}`);
                    }
                } else {
                     // This log should ideally not appear now if caller is fixed
                     console.log(`[mapToSimplifiedHotel] Individual Offer ${index} missing rawTotal or currency.`);
                }
            } catch (e) {
                 console.error(`[mapToSimplifiedHotel] Error processing price for individual offer ${index}:`, e);
            }
        });

        // If we found a minimum offer, use its raw data
        if (minOfferData !== null) {
             // --- Alternative Explicit Typing ---
            // Assign to a new variable with the explicit type
            const confirmedOfferData: { total: string, currency: string } = minOfferData;
            // Now use the new variable
            priceRange = `${confirmedOfferData.currency} ${confirmedOfferData.total}`;
            // --- End Alternative ---
            console.log(`[mapToSimplifiedHotel] Setting priceRange from min offer: ${priceRange}`);
        } else {
             console.log(`[mapToSimplifiedHotel] No valid minimum price found. Keeping default: ${priceRange}`);
        }
    } else {
         console.log(`[mapToSimplifiedHotel] No offers provided or empty offers array. Keeping default: ${priceRange}`);
    }

    console.log(`[mapToSimplifiedHotel] Final priceRange for Hotel ID ${hotel.hotelId}: "${priceRange}"`);

    // --- Log the hotel object structure for coordinate debugging ---
    console.log(`[mapToSimplifiedHotel] Hotel data for coord extraction (ID: ${hotel.hotelId}):`, JSON.stringify(hotel, null, 2));
    // --- End Log ---

    // --- Refined Coordinate Extraction ---
    let latitude: number | undefined = undefined;
    let longitude: number | undefined = undefined;

    // >>> PRIORITIZE coordinates passed directly in the hotel object <<<
    if (typeof hotel.latitude === 'number' && typeof hotel.longitude === 'number') {
        latitude = hotel.latitude;
        longitude = hotel.longitude;
        console.log(`[mapToSimplifiedHotel] Using directly passed coords`);
    }
    // Fallback: Try extracting from top-level geoCode (common structure)
    else if (typeof (hotel as any).geoCode?.latitude === 'number' && typeof (hotel as any).geoCode?.longitude === 'number') {
        latitude = (hotel as any).geoCode.latitude;
        longitude = (hotel as any).geoCode.longitude;
        console.log(`[mapToSimplifiedHotel] Extracted coords from hotel.geoCode`);
    }
    // Fallback: Try extracting from address.geoCode (less common but possible)
    else if (typeof (hotel as any).address?.geoCode?.latitude === 'number' && typeof (hotel as any).address?.geoCode?.longitude === 'number') {
        latitude = (hotel as any).address.geoCode.latitude;
        longitude = (hotel as any).address.geoCode.longitude;
        console.log(`[mapToSimplifiedHotel] Extracted coords from hotel.address.geoCode`);
    }
    else {
        console.warn(`[mapToSimplifiedHotel] Could not find valid coordinates for Hotel ID: ${hotel.hotelId}`);
    }

    // Validate extracted coordinates
    if (latitude !== undefined && (isNaN(latitude) || latitude < -90 || latitude > 90)) latitude = undefined;
    if (longitude !== undefined && (isNaN(longitude) || longitude < -180 || longitude > 180)) longitude = undefined;

    console.log(`[mapToSimplifiedHotel] Final Coords for Hotel ID ${hotel.hotelId} - Lat: ${latitude}, Lng: ${longitude}`);
    // --- End Refined Coordinate Extraction ---

    // --- Call the correct extractRoomInfo, passing the individual offers array ---
    const roomInfo = this.extractRoomInfo(offers) || [];
    // ---

    const ratingValue = typeof hotel.rating === 'string' ? parseFloat(hotel.rating) : hotel.rating;
    const amenities = hotel.amenities || [];

    return {
        id: hotel.hotelId,
        name: hotel.name || 'Unknown Hotel Name',
        rating: ratingValue !== undefined && !isNaN(ratingValue) ? ratingValue : undefined,
        priceRange: priceRange,
        imageUrl: hotel.media?.[0]?.uri,
        city: hotel.address?.cityName,
        latitude: latitude,
        longitude: longitude,
        amenities: amenities.length > 0 ? amenities : undefined,
        roomInfo: roomInfo.length > 0 ? roomInfo : undefined,
    };
  }

  /**
   * Extract *detailed* room information from offers
   */
  extractRoomInfo(offers?: OfferType[]): SimplifiedHotel['roomInfo'] {
      // This function should now work correctly as it also expects the array of individual offers
      type DetailedRoomInfo = NonNullable<SimplifiedHotel['roomInfo']>[number];
      const roomInfo: DetailedRoomInfo[] = [];
      if (!offers || !Array.isArray(offers) || offers.length === 0) return roomInfo;

      const roomTypeMap = new Map<string, DetailedRoomInfo>();

      // Loop through individual offers
      offers.forEach(individualOffer => {
           if (!individualOffer || !individualOffer.room?.typeEstimated?.category) return;

           // Extract data directly from individualOffer
           const roomType = individualOffer.room.typeEstimated.category;
           const bedType = individualOffer.room.typeEstimated.bedType || undefined;
           const description = individualOffer.room?.description?.text || '';
           const boardType = (individualOffer as any).boardType || undefined; // Use assertion if OfferType is wrong
           const cancellationDeadline = (individualOffer as any).policies?.cancellations?.[0]?.deadline || undefined; // Use assertion
           const rawTotal = individualOffer.price?.total;
           const currency = individualOffer.price?.currency || 'USD';

           let price: number | undefined = undefined;
            if (rawTotal && String(rawTotal).trim() !== "") {
                 let processString = String(rawTotal).replace(',', '.');
                 const numericString = processString.replace(/[^0-9.-]+/g, "");
                 const parsedPrice = parseFloat(numericString);
                 if(!isNaN(parsedPrice)) price = parsedPrice;
            }

            const key = `${roomType}-${bedType || 'any'}`;
            const currentRoomData: DetailedRoomInfo = {
                type: roomType, price, priceTotal: rawTotal, currency, bedType, description, boardType, cancellationDeadline
            };

            const existingEntry = roomTypeMap.get(key);
            if (!existingEntry || (price !== undefined && (existingEntry.price === undefined || price < existingEntry.price))) {
                 roomTypeMap.set(key, currentRoomData);
            }
      });

      roomTypeMap.forEach(value => roomInfo.push(value));
      roomInfo.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
      return roomInfo;
  }

  /**
   * Extract price ranges from offer data
   */
  extractPriceRanges(offersData: Record<string, OfferType[]>): { minPrice: number | null; maxPrice: number | null } {
    let minPrice: number | null = null;
    let maxPrice: number | null = null;
    
    // Iterate through all offers for all hotels
    Object.values(offersData).forEach((hotelOffers: OfferType[]) => {
      if (!Array.isArray(hotelOffers) || hotelOffers.length === 0) return;
      
      hotelOffers.forEach(offer => {
        if (!offer?.price?.total) return;
        
        const price = parseFloat(offer.price.total);
        if (isNaN(price)) return;
        
        // Update min/max prices
        if (minPrice === null || price < minPrice) minPrice = price;
        if (maxPrice === null || price > maxPrice) maxPrice = price;
      });
    });
    
    return { minPrice, maxPrice };
  }
  
  /**
   * Extract room types and create a summary
   */
  extractRoomPriceSummary(offersData: Record<string, OfferType[]>, hotels: any[]): string {
    // Get all room types and categories
    const roomTypes = new Set<string>();
    const roomCategories = new Map<string, number[]>();
    
    Object.entries(offersData).forEach(([_hotelId, hotelOffers]: [string, OfferType[]]) => {
      if (!Array.isArray(hotelOffers) || hotelOffers.length === 0) return;
      
      hotelOffers.forEach(offer => {
        if (!offer?.room?.typeEstimated?.category) return;
        
        const roomCategory = offer.room.typeEstimated.category;
        const price = parseFloat(offer.price?.total || '0');
        
        roomTypes.add(roomCategory);
        
        if (!roomCategories.has(roomCategory)) {
          roomCategories.set(roomCategory, [price]);
        } else {
          roomCategories.get(roomCategory)?.push(price);
        }
      });
    });
    
    // Create summary text
    if (roomTypes.size === 0) return '';
    
    let summary = 'Room options include ';
    const roomDetails: string[] = [];
    
    roomCategories.forEach((prices, category) => {
      if (prices.length > 0) {
        const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
        roomDetails.push(`${category} (avg. $${Math.round(avgPrice)}/night)`);
      } else {
        roomDetails.push(category);
      }
    });
    
    summary += roomDetails.join(', ') + '.';
    return summary;
  }
} 